/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-27
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  LockOpen as LockOpenIcon,
  Rocket as RocketIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Celebration as CelebrationIcon,
  Business as BusinessIcon,
  MenuBook as MenuBookIcon,
  Palette as PaletteIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Campaign as CampaignIcon,
  AccountBalance as QuickBooksIcon,
  CalendarMonth as SchedulingIcon,
  ShoppingCart as ShoppingCartIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import Header from '../../layout/Header';
import { MobileToolsGrid } from '../../navigation/mobile/MobileToolsMenu';
import { Tool, ToolCategory } from '../../../types/navigation';
import { useStudioThemeAccess } from '../../../hooks/useStudioThemeAccess';
import { YouTubeVideoButton } from '../../common';

/**
 * Props for the Tools component.
 */
interface ToolsProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * Tools page component.
 * Displays available dance studio tools with mobile-optimized interface.
 * Integrates with the mobile tools grid for touch-friendly navigation.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The tools page component
 *
 * @example
 * return (
 *   <Tools />
 * );
 */
const Tools: React.FC<ToolsProps> = ({ className }) => {
  const { t } = useTranslation('tools');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get studio theme access information
  const { hasThemeAccess } = useStudioThemeAccess();

  // Categories for tools - organized by subscription tier with tier-specific styling
  const mockCategories: ToolCategory[] = [
    {
      id: 'free',
      name: 'Free Tools',
      icon: <LockOpenIcon />,
      description: 'Available to all users',
      order: 1,
      color: '#4CAF50', // Green - accessible to all
    },
    {
      id: 'starter',
      name: 'Starter Plan Tools',
      icon: <RocketIcon />,
      description: 'Requires Starter plan ($27/month) or higher',
      order: 2,
      color: '#2196F3', // Blue - entry-level professional
    },
    {
      id: 'growth',
      name: 'Growth Plan Tools',
      icon: <TrendingUpIcon />,
      description: 'Requires Growth plan ($37/month) or higher',
      order: 3,
      color: '#9C27B0', // Purple - mid-tier
    },
    {
      id: 'pro',
      name: 'Pro Plan Tools',
      icon: <StarIcon />,
      description: 'Requires Pro plan ($67/month)',
      order: 4,
      color: '#FF9800', // Gold/Amber - premium tier
    },
  ];

  // Available tools organized by subscription tier
  const mockTools: Tool[] = [
    // FREE TIER - Available to all users
    {
      id: 'getting-started-guide',
      name: t('gettingStarted.name'),
      description: t('gettingStarted.description'),
      categoryId: 'free',
      status: 'available',
      icon: <MenuBookIcon />,
      path: '/getting-started',
      tutorialVideos: [
        {
          videoId: 'QYDrwRvE2bU',
          title: 'How to Update Your Profile on tools.dance',
          label: 'Profile Tutorial',
        },
        {
          videoId: 'Ke3mmXrMpo0',
          title: 'How to Create a New Studio on tools.dance',
          label: 'Studio Tutorial',
        },
      ],
    },
    {
      id: 'studios',
      name: t('studios.name'),
      description: t('studios.description'),
      categoryId: 'free',
      status: 'available',
      icon: <BusinessIcon />,
      path: '/studios',
      tutorialVideos: [
        {
          videoId: 'Ke3mmXrMpo0',
          title: 'How to Create a New Studio on tools.dance',
          label: 'Studio Tutorial',
        },
        {
          videoId: 's9RZo_gHoNQ',
          title: 'How to Invite Staff Members on tools.dance',
          label: 'Invitations Tutorial',
        },
      ],
    },
    {
      id: 'studio-theme',
      name: t('studioTheme.name'),
      description: t('studioTheme.description'),
      categoryId: 'free',
      status: 'available',
      icon: <PaletteIcon />,
      path: '/studio-theme-selector',
      tutorialVideos: [
        {
          videoId: '3DYvTkQC_ko',
          title: 'Studio Theme Tutorial',
          label: 'Theme Tutorial',
        },
      ],
    },
    {
      id: 'team-bios',
      name: t('teamBios.name'),
      description: t('teamBios.description'),
      categoryId: 'free',
      status: 'available',
      icon: <GroupIcon />,
      path: '/team-bios-selector',
      tutorialVideos: [
        {
          videoId: 'I1paHUh7s4Q',
          title: 'Team Bios Tutorial',
          label: 'Team Bios Tutorial',
        },
        {
          videoId: 'QYDrwRvE2bU',
          title: 'How to Update Your Profile on tools.dance',
          label: 'Profile Tutorial',
        },
      ],
    },
    {
      id: 'students',
      name: t('students.name'),
      description: t('students.description'),
      categoryId: 'free',
      status: 'available',
      icon: <SchoolIcon />,
      path: '/students-selector',
      tutorialVideos: [
        {
          videoId: '8NGyiIKLADQ',
          title: 'Students Tool Tutorial',
          label: 'Students Tutorial',
        },
      ],
    },
    {
      id: 'happy-birthday-widget',
      name: t('happyBirthdayWidget.name'),
      description: t('happyBirthdayWidget.description'),
      categoryId: 'free',
      status: 'available',
      icon: <CelebrationIcon />,
      path: '/happy-birthday-widget-selector',
      tutorialVideos: [
        {
          videoId: 'bdsDgc7hlaM',
          title: 'Happy Birthday Widget Tutorial',
          label: 'Birthday Widget Tutorial',
        },
      ],
    },
    {
      id: 'announcements',
      name: 'Announcements',
      description: 'Display custom announcements on your public page',
      categoryId: 'free',
      status: 'available',
      icon: <CampaignIcon />,
      path: '/announcements-selector',
      tutorialVideos: [
        {
          videoId: 'toVq0I4my3Q',
          title: 'How to Use Announcements on tools.dance',
          label: 'Announcements Tutorial',
        },
      ],
    },

    // GROWTH TIER - Requires Growth plan
    {
      id: 'financial-insights',
      name: 'Financial Insights Dashboard',
      description:
        'Comprehensive financial analytics powered by QuickBooks Online',
      categoryId: 'growth',
      status: 'available',
      icon: <TrendingUpIcon />,
      path: '/financial-insights-selector',
      subscriptionRequirement: { minimumPlan: 'growth' },
    },
    {
      id: 'quickbooks-sales-receipts',
      name: 'QuickBooks Sales Receipts',
      description:
        'Upload sales receipts from Dance Studio Pro to QuickBooks Online',
      categoryId: 'growth',
      status: 'available',
      icon: <QuickBooksIcon />,
      path: '/quickbooks',
      subscriptionRequirement: { minimumPlan: 'growth' },
      tutorialVideos: [
        {
          videoId: 'TQnYCa1qWIk',
          title: 'How to Upload QuickBooks Sales Receipts on tools.dance',
          label: 'QuickBooks Tutorial',
        },
      ],
    },

    // STARTER TIER - Requires Starter plan
    {
      id: 'class-essentials',
      name: 'Class Essentials',
      description:
        "Send parents personalized checklists of required items for their student's classes",
      categoryId: 'starter',
      status: 'available',
      icon: <ChecklistIcon />,
      path: '/class-essentials',
      subscriptionRequirement: { minimumPlan: 'starter' },
    },
    {
      id: 'shopping-list',
      name: 'Shopping List',
      description:
        'Manage shopping lists for different stores with organized item tracking',
      categoryId: 'starter',
      status: 'available',
      icon: <ShoppingCartIcon />,
      path: '/shopping-list',
      subscriptionRequirement: { minimumPlan: 'starter' },
    },

    // PRO TIER - Requires Pro plan
    {
      id: 'scheduling',
      name: 'Class Scheduling',
      description:
        'Automated class scheduling with teacher and room assignments',
      categoryId: 'pro',
      status: 'available',
      icon: <SchedulingIcon />,
      path: '/scheduling',
      subscriptionRequirement: { minimumPlan: 'pro' },
    },
  ];

  const handleToolSelect = (toolId: string) => {
    const selectedTool = mockTools.find(tool => tool.id === toolId);
    if (selectedTool && selectedTool.status === 'available') {
      // Special handling for Studio Theme tool
      if (toolId === 'studio-theme') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page
          navigate('/studios');
          return;
        }

        // All users with theme access go to the same theme page
        navigate('/theme');
        return;
      }

      // Special handling for Team Bios tool
      if (toolId === 'team-bios') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Team Bios',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Team Bios page - will show studio selector if multiple
        navigate('/team-bios');
        return;
      }

      // Special handling for Students tool
      if (toolId === 'students') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager/admin access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Students',
                requiredRoles: ['owner', 'manager', 'admin'],
              },
            },
          });
          return;
        }

        // Navigate to Students page - will show studio selector if multiple
        navigate('/students');
        return;
      }

      // Special handling for Happy Birthday Widget tool
      if (toolId === 'happy-birthday-widget') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Birthday Widget',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Birthday Widget settings page
        navigate('/happy-birthday-widget');
        return;
      }

      // Special handling for Announcements tool
      if (toolId === 'announcements') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Announcements',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Announcements page
        navigate('/announcements');
        return;
      }

      // Special handling for QuickBooks Sales Receipts tool
      if (toolId === 'quickbooks-sales-receipts') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager/admin access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'QuickBooks Sales Receipts',
                requiredRoles: ['owner', 'manager', 'admin'],
              },
            },
          });
          return;
        }

        // Navigate to QuickBooks page
        navigate('/quickbooks');
        return;
      }

      // Special handling for Financial Insights Dashboard tool
      if (toolId === 'financial-insights') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Financial Insights',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Financial Insights Dashboard
        // Since this is studio-specific, we'll need to check for selected studio
        // For now, navigate to a selector page similar to other tools
        navigate('/financial-insights');
        return;
      }

      // Special handling for Class Essentials tool
      if (toolId === 'class-essentials') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Class Essentials',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Class Essentials page
        navigate('/class-essentials');
        return;
      }

      // Special handling for Scheduling tool
      if (toolId === 'scheduling') {
        // Smart navigation based on user's studio ownership/management status
        if (!hasThemeAccess) {
          // User has no owner/manager access - redirect to studios page with context
          navigate('/studios', {
            state: {
              toolAccessDenied: {
                toolName: 'Class Scheduling',
                requiredRoles: ['owner', 'manager'],
              },
            },
          });
          return;
        }

        // Navigate to Scheduling page
        navigate('/scheduling');
        return;
      }

      // Standard navigation for other tools
      if (selectedTool.path) {
        navigate(selectedTool.path);
      }
    }
  };

  // Filter tools based on subscription access
  const getToolsWithAccess = () =>
    mockTools.map(tool => {
      if (!tool.subscriptionRequirement) {
        return tool; // Free tools always available
      }

      // For tools with subscription requirements, check access
      return {
        ...tool,
        hasAccess: true, // Will be determined by FeatureGate component
      };
    });

  const toolsWithAccess = getToolsWithAccess();

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container
        maxWidth="lg"
        className={className}
        sx={{
          py: 3,
          // Add bottom padding for mobile to account for bottom tab bar
          pb: isMobile ? 10 : 3,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
            }}
          >
            {t('title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.6,
            }}
          >
            {t('description')}
          </Typography>
        </Box>

        {/* Upgrade Tutorial Section */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Want access to premium tools? Watch our tutorial to learn how to
            upgrade your plan.
          </Typography>
          <YouTubeVideoButton
            videoId="5vt_utY1LDw"
            videoTitle="How to Upgrade Your Plan on tools.dance"
            buttonText="Watch Upgrade Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Tools Grid */}
        <MobileToolsGrid
          tools={toolsWithAccess}
          categories={mockCategories}
          onToolSelect={handleToolSelect}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
      </Container>
    </Box>
  );
};

export default Tools;
